// lib/canny.ts

export const blurAlphaChannel = (imageData: ImageData, blurRadius: number) => {
    // This function performs an inward blur on the alpha channel of an ImageData.
    // It modifies the alpha values of pixels that are not fully transparent,
    // blurring the edges of the mask inwards.
    // Pixels that were originally fully transparent (alpha = 0) will remain unchanged.
    // Newly blurred area have a minimum alpha of 1 to allow mask regeneration from it.

    const width = imageData.width;
    const height = imageData.height;
    const data = new Uint8ClampedArray(imageData.data); 
    const blurredData = new Uint8ClampedArray(data); // Create a copy to store blurred data
    const originalAlphaData = new Uint8ClampedArray(width * height); // Store original alpha values

    // Store original alpha values
    for (let y = 0; y < height; y++) {
        const yw = y * width;
        for (let x = 0; x < width; x++) {
            const pixelIndex = (yw + x) * 4;
            const alpha = data[pixelIndex + 3];
            const originalAlphaIndex = yw + x;
            if (alpha !== undefined && originalAlphaData[originalAlphaIndex] !== undefined) {
                originalAlphaData[originalAlphaIndex] = alpha;
            }
        }
    }


    // 1. Invert Mask Alpha (for inward blur)
    const invertedAlphaData = new Uint8ClampedArray(data);
    // 1.1. Find Mask Border Pixels and set them to 255 in invertedAlphaData
    for (let y = 0; y < height; y++) {
        const yw = y * width;
        for (let x = 0; x < width; x++) {
            const pixelIndex = (yw + x) * 4;
            const alpha = data[pixelIndex + 3];
            if (alpha !== undefined && invertedAlphaData[pixelIndex + 3] !== undefined) {
                if (alpha > 0) { // If pixel is not transparent in original mask
                    invertedAlphaData[pixelIndex + 3] = 255 - alpha; // Invert alpha channel
                } else {
                    invertedAlphaData[pixelIndex + 3] = 255; // Keep transparent pixels as opaque after inversion for inward blur
                }
            }
        }
    }
    const modifiedData = new Uint8ClampedArray(invertedAlphaData); // Use inverted data for blurring



    // Horizontal Blur (Alpha Channel only) - Opaque Border
    for (let y = 0; y < height; y++) {
        const yw = y * width;
        for (let x = 0; x < width; x++) {
            let alphaSum = 0;
            let count = 0;
            for (let i = -blurRadius; i <= blurRadius; i++) {
                const neighborX = x + i;
                let neighborAlpha: number | undefined; // Declare neighborAlpha here

                if (neighborX >= 0 && neighborX < width) {
                    neighborAlpha = modifiedData[(yw + neighborX) * 4 + 3]; // Get alpha from image
                } else {
                    neighborAlpha = 255; // Treat outside as opaque (alpha 255)
                }
                if (neighborAlpha !== undefined) {
                    alphaSum += neighborAlpha;
                    count++;
                }
            }
            const blurredDataIndex = (yw + x) * 4 + 3;
            if (blurredData[blurredDataIndex] !== undefined) {
                blurredData[blurredDataIndex] = alphaSum / count; // Average alpha
            }
        }
    }

    // Vertical Blur (Alpha Channel only) - Opaque Border - using already horizontally blurred alpha values
    const tempData = new Uint8ClampedArray(blurredData); // Copy horizontally blurred data for vertical pass
    for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
            let alphaSum = 0;
            let count = 0;
            for (let j = -blurRadius; j <= blurRadius; j++) {
                const neighborY = y + j;
                let neighborAlpha: number | undefined; // Declare neighborAlpha here

                if (neighborY >= 0 && neighborY < height) {
                    neighborAlpha = tempData[((neighborY) * width + x) * 4 + 3]; // Get alpha from horizontally blurred data
                } else {
                    neighborAlpha = 255; // Treat outside as opaque (alpha 255)
                }
                if (neighborAlpha !== undefined) {
                    alphaSum += neighborAlpha;
                    count++;
                }
            }
            const blurredDataIndex = (y * width + x) * 4 + 3;
            if (blurredData[blurredDataIndex] !== undefined) {
                blurredData[blurredDataIndex] = alphaSum / count; // Average alpha
            }
        }
    }


    // 3. Invert Mask Alpha Back
    for (let i = 0; i < blurredData.length; i += 4) {
        const alpha = blurredData[i + 3];
        if (alpha !== undefined) {
            blurredData[i + 3] = 255 - alpha; // Invert alpha channel back
        }
    }

    // 4. Restore original alpha for fully transparent pixels
    for (let y = 0; y < height; y++) {
        const yw = y * width;
        for (let x = 0; x < width; x++) {
            const originalAlphaIndex = yw + x;
            const blurredDataIndex = (yw + x) * 4 + 3;
            const originalAlpha = originalAlphaData[originalAlphaIndex];
            const blurredAlpha = blurredData[blurredDataIndex];

            if (originalAlpha !== undefined && blurredAlpha !== undefined) {
                if (originalAlpha === 0) {
                    blurredData[blurredDataIndex] = 0; // Restore original alpha if it was 0
                } else {
                    blurredData[blurredDataIndex] = Math.max((2 * (blurredAlpha - 127)) - 1, 1);
                }
            }
        }
    }


    return new ImageData(blurredData, width, height);
};

